'use client';

import { listByProductIds } from '@/api/public';
import { useAppNavigation } from '@/hooks/useAppNavigation';
import { useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import Banner from './components/Banner';
import PersonalityGoodsLists from './components/GoodsLists';
import PhotoWall from './components/PhotoWall';
import PersonalityProductLists from './components/ProductLists';
import WaterFull from './components/WaterFull';
import { usePersonalityFitTracking } from './hooks/usePersonalityFitTracking';

export interface PersonalityFitClientProps {
  initialData: Record<string, any>;
}

export default function PersonalityFitClient({ initialData }: PersonalityFitClientProps) {
  const [pageData, setPageData] = useState<any>(initialData || {});
  const data = useMemo(() => pageData, [pageData]);
  const photoWallData = useMemo(() => {
    const part = (data as any)?.swiper_part || {};
    const lists =
      part?.swiper_lists || (data as any)?.swiper_lists || (data as any)?.swiperLists || [];
    const bgImg = part?.bg_img || (data as any)?.swiper_banner || (data as any)?.swiperBanner || '';
    return { swiper_lists: lists, bg_img: bgImg } as any;
  }, [data]);
  const bottomPart = useMemo(() => (data as any)?.bottom_part || {}, [data]);
  const { navigate } = useAppNavigation();
  const { trackMktClick, trackMktExp } = usePersonalityFitTracking();
  const [bottomRef, bottomInView] = useInView({ threshold: 0.5, triggerOnce: true });

  // 客户端兜底合并：对齐 Vue getProductData 的行为
  useEffect(() => {
    const mergeByIds = async (arr: any[], opts?: { noSoldOut?: boolean }) => {
      const ids: number[] = Array.isArray(arr)
        ? arr.filter((i: any) => i && (i as any).id).map((i: any) => Number((i as any).id))
        : [];
      if (!ids.length) return arr;
      try {
        const params: any = { product_ids: ids.join(',') };
        if (opts?.noSoldOut) {
          params.in_stock = 1;
          params.is_sale = 1;
        }
        const resp: any = await listByProductIds(params);
        const list: any[] = Array.isArray(resp?.list) ? resp.list : [];
        if (!list.length) return arr;
        const merged = arr.map((item: any) => {
          if (!item?.id) return item;
          const found = list.find((x: any) => Number(x?.id) === Number(item?.id));
          // 关键：先保留原始占位（含 type/jump_*），再用接口详情补齐（含 image/price_info/...）
          return found ? { ...item, ...found } : item;
        });
        // 保险：当要求 noSoldOut 时，过滤掉下架/无库存商品
        if (opts?.noSoldOut) {
          return merged.filter((it: any) => {
            if (Number(it?.type) !== 2) return true;
            const statusOk = (it?.status ?? 1) === 1;
            const stockOk = (it?.stock ?? 1) > 0;
            return statusOk && stockOk;
          });
        }
        return merged;
      } catch {
        /* noop */
        return arr;
      }
    };

    (async () => {
      // product_lists 合并（服务端已尝试，这里兜底）
      if (Array.isArray((initialData as any)?.product_lists)) {
        const merged = await mergeByIds((initialData as any).product_lists);
        if (merged !== (initialData as any).product_lists) {
          setPageData((prev: any) => ({ ...prev, product_lists: merged }));
        }
      }
      // waterFull 合并（Vue端客户端也会合并，这里补齐）
      if (Array.isArray((initialData as any)?.waterFull)) {
        const mergedWF = await mergeByIds((initialData as any).waterFull, { noSoldOut: true });
        if (mergedWF !== (initialData as any).waterFull) {
          setPageData((prev: any) => ({ ...prev, waterFull: mergedWF }));
        }
      }
    })();
  }, [initialData]);

  // 底部按钮区域曝光
  if (bottomInView && bottomPart?.jump_id) {
    try {
      trackMktExp({ mkt_name: 'Waterfall_Area_1_Btn', group_id: String(bottomPart.jump_id) });
    } catch {
      /* noop */
    }
  }

  const handleViewMoreClick = () => {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      window.sessionStorage.setItem('zl_goodsListEnterPageScene', '活动专题页');
    }
    if (bottomPart?.jump_id) {
      try {
        trackMktClick({ mkt_name: 'Waterfall_Area_1_Btn', group_id: String(bottomPart.jump_id) });
      } catch {
        /* noop */
      }
      navigate(`/goods-list/${bottomPart.jump_id}`);
    }
  };

  return (
    <div className="w-full">
      {data?.banner1 && <Banner bannerData={(data as any).banner1} bannerIndex={0} />}

      {data?.banner2 && (
        <Banner bannerData={(data as any).banner2} bannerIndex={1} className="h-40" />
      )}

      {Array.isArray((data as any).product_lists) && (data as any).product_lists.length > 0 && (
        <PersonalityProductLists productLists={(data as any).product_lists} />
      )}

      {Array.isArray((photoWallData as any).swiper_lists) &&
        (photoWallData as any).swiper_lists.length > 0 && <PhotoWall swiperPart={photoWallData} />}

      {data?.list_part?.group && (
        <PersonalityGoodsLists
          listPart={(data as any).list_part}
          serverList={(data as any)?.goodsListServer?.list || []}
        />
      )}

      {/* WaterFull */}
      {Array.isArray((data as any).waterFull) && (data as any).waterFull.length > 0 && (
        <WaterFull waterFull={(data as any).waterFull} bannerThreeData={(data as any).banner3} />
      )}

      {/* Bottom View More */}
      {bottomPart?.jump_id && (
        <div
          ref={bottomRef}
          className="bottom-part flex flex-col items-center justify-center px-[10.156vw] pb-[4.167vw]"
        >
          <a
            href={`/goods-list/${bottomPart.jump_id}`}
            className="banner-wrap mb-[2.75vw] block"
            onClick={(e) => {
              e.preventDefault();
              handleViewMoreClick();
            }}
          >
            <div className="btn w-[27.083vw] h-[3.646vw] rounded-[8px] border-[2px] border-[#BDAD8D] box-border text-center leading-[3.646vw] text-[1.563vw] text-[#BDAD8D] font-bb">
              {bottomPart.button_word}
            </div>
          </a>
          {bottomPart?.description && (
            <p className="word text-center text-[#BDAD8D] text-[0.938vw] leading-[1.615vw]">
              {bottomPart.description}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
